import { pure } from '@core/helpers/pure';
import { TuiBlock } from '@taiga-ui/kit';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { Nullable } from '@core/helpers';
import {
  RequestDevice,
  RequestDeviceCutoffSwitch,
  RequestDeviceMountType,
  RequestDeviceOnboardVoltage,
  RequestDeviceWithAdditionalSettings,
  RequestDeviceWithoutAdditionalSettings,
} from '@core/types';
import { DeviceDetectorService } from '@core/services';
import { RequestDevicesService } from '@core/services/request/request-devices.service';
import { TuiDialogContext, TuiIcon, TuiButton, TuiRadio } from '@taiga-ui/core';
import { POLYMORPHEUS_CONTEXT } from '@taiga-ui/polymorpheus';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgClass } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';

enum FormFields {
  MountType = 'mountType',
  OnboardVoltage = 'onboardVoltage',
  CutoffSwitch = 'cutoffSwitch',
}

@Component({
  selector: 'x-request-device-additional-settings-modal',
  imports: [
    ReactiveFormsModule,
    TuiBlock,
    TuiRadio,
    TuiIcon,
    NgClass,
    TuiButton,
    TranslatePipe,
  ],
  templateUrl: './request-device-additional-settings-modal.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RequestDeviceAdditionalSettingsModalComponent implements OnInit {
  private readonly requestDevicesService = inject(RequestDevicesService);
  private readonly formBuilder = inject(FormBuilder);
  private readonly destroyRef = inject(DestroyRef);
  private readonly deviceService = inject(DeviceDetectorService);

  private readonly context = inject(POLYMORPHEUS_CONTEXT, {
    optional: false,
  }) as TuiDialogContext<
    Partial<RequestDeviceWithAdditionalSettings>,
    {
      deviceData:
        | RequestDeviceWithoutAdditionalSettings
        | Omit<RequestDeviceWithoutAdditionalSettings, 'installDate'>
        | RequestDeviceWithAdditionalSettings;
    }
  >;
  protected readonly $isDesktop = this.deviceService.$isDesktop;

  public $isMountTypeFormFieldShown = signal(false);

  public $isOnboardVoltageFormFieldShown = signal(false);

  public $isCutoffSwitchFormFieldShown = signal(false);

  public loading = signal(true);

  public readonly mountTypeOptions = Object.values(RequestDeviceMountType);

  public readonly onboardVoltageOptions = Object.values(
    RequestDeviceOnboardVoltage,
  );

  public readonly cutoffSwitchOptions = Object.values(
    RequestDeviceCutoffSwitch,
  );

  FormFields = FormFields;

  formGroup = this.formBuilder.group({
    [FormFields.MountType]:
      this.formBuilder.control<Nullable<RequestDeviceMountType>>(null),

    [FormFields.OnboardVoltage]:
      this.formBuilder.control<Nullable<RequestDeviceOnboardVoltage>>(null),

    [FormFields.CutoffSwitch]:
      this.formBuilder.control<Nullable<boolean>>(null),
  });

  public ngOnInit(): void {
    const mountType =
      'mountType' in this.deviceData ? this.deviceData.mountType : null;

    if (mountType) {
      this.mountTypeFormField.setValue(
        mountType as Nullable<RequestDeviceMountType>,
      );
    }

    const onboardVoltage =
      'onboardVoltage' in this.deviceData
        ? this.deviceData.onboardVoltage
        : null;

    if (onboardVoltage) {
      this.onboardVoltageFormField.setValue(
        onboardVoltage as Nullable<RequestDeviceOnboardVoltage>,
      );
    }

    const cutoffSwitch =
      'cutoffSwitch' in this.deviceData ? this.deviceData.cutoffSwitch : null;

    if (cutoffSwitch !== null) {
      this.cutoffSwitchFormField.setValue(cutoffSwitch as Nullable<boolean>);
    }

    this.getDeviceModelId$(this.deviceData.name)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(deviceModel => {
        if (deviceModel?.isTelemetry === false) {
          this.mountTypeFormField.setValidators(Validators.required);
          this.onboardVoltageFormField.clearValidators();
          this.cutoffSwitchFormField.clearValidators();
          this.onboardVoltageFormField.setValue(null);
          this.cutoffSwitchFormField.setValue(null);
          this.$isMountTypeFormFieldShown.set(true);
          this.$isOnboardVoltageFormFieldShown.set(false);
          this.$isCutoffSwitchFormFieldShown.set(false);
        } else if (deviceModel?.isTelemetry === true) {
          this.mountTypeFormField.clearValidators();
          this.onboardVoltageFormField.setValidators(Validators.required);
          this.cutoffSwitchFormField.setValidators(Validators.required);
          this.mountTypeFormField.setValue(null);
          this.$isMountTypeFormFieldShown.set(false);
          this.$isOnboardVoltageFormFieldShown.set(true);
          this.$isCutoffSwitchFormFieldShown.set(true);
        } else {
          this.mountTypeFormField.clearValidators();
          this.onboardVoltageFormField.clearValidators();
          this.cutoffSwitchFormField.clearValidators();

          this.$isMountTypeFormFieldShown.set(true);
          this.$isOnboardVoltageFormFieldShown.set(true);
          this.$isCutoffSwitchFormFieldShown.set(true);
        }
        this.mountTypeFormField.updateValueAndValidity();
        this.onboardVoltageFormField.updateValueAndValidity();
        this.cutoffSwitchFormField.updateValueAndValidity();
        this.loading.set(false);
      });
  }

  @pure
  public get deviceData():
    | RequestDeviceWithoutAdditionalSettings
    | Omit<RequestDeviceWithoutAdditionalSettings, 'installDate'>
    | RequestDeviceWithAdditionalSettings {
    return this.context.data.deviceData;
  }

  private getDeviceModelId$(sim1: RequestDevice['name']) {
    return this.requestDevicesService.getDeviceModelId$(sim1);
  }

  @pure
  get mountTypeFormField() {
    return this.formGroup.controls[FormFields.MountType];
  }

  @pure
  get onboardVoltageFormField() {
    return this.formGroup.controls[FormFields.OnboardVoltage];
  }

  @pure
  get cutoffSwitchFormField() {
    return this.formGroup.controls[FormFields.CutoffSwitch];
  }

  toggleMountType(event: Event, mountOption: string) {
    if (this.mountTypeFormField.value === mountOption) {
      event.preventDefault();
      this.mountTypeFormField.setValue(null);
    }
  }

  toggleOnboardVoltage(event: Event, voltageOption: string) {
    if (this.onboardVoltageFormField.value === voltageOption) {
      event.preventDefault();
      this.onboardVoltageFormField.setValue(null);
    }
  }

  toggleCutoffSwitch(event: Event, cutoffSwitchOption: boolean) {
    if (this.cutoffSwitchFormField.value === cutoffSwitchOption) {
      event.preventDefault();
      this.cutoffSwitchFormField.setValue(null);
    }
  }

  @pure
  getMountTypeTranslationKey(mountOption: string) {
    switch (mountOption) {
      case RequestDeviceMountType.METAL_MOUNT:
        return 'request_form.devices.mount_options.metal_mount';
      case RequestDeviceMountType.TIE:
        return 'request_form.devices.mount_options.ties';
      case RequestDeviceMountType.DOUBLE_SIDED_TAPE:
        return 'request_form.devices.mount_options.double_sided_tape';
      case RequestDeviceMountType.MAGNETS:
        return 'request_form.devices.mount_options.magnets';
      default:
        return '';
    }
  }

  @pure
  getCutoffSwitchTranslationKey(cutoffSwitchOption: boolean) {
    return `request_form.devices.cutoff_switch_options.${cutoffSwitchOption ? 'yes' : 'no'}`;
  }

  submit() {
    if (this.formGroup.invalid) {
      return;
    }

    const mountType = this.mountTypeFormField.value;
    const onboardVoltage = this.onboardVoltageFormField.value;
    const cutoffSwitch = this.cutoffSwitchFormField.value;

    const additionalSettings: Partial<RequestDeviceWithAdditionalSettings> = {
      mountType,
      onboardVoltage,
      cutoffSwitch,
    };

    const dataToSend = {
      ...this.deviceData,
      ...additionalSettings,
    };

    this.context.completeWith(dataToSend);
  }
}
